/**
 * Getting a file OFF the phone (D-020).
 *
 * ── Why this is not one line of `<a download>` ──────────────────────────────
 * The device this feature exists for is an iPhone running the app from a Home
 * Screen icon, and that is precisely where a download link is least reliable: in
 * standalone display mode iOS has historically opened the blob in a viewer, or
 * done nothing at all, with no error for the app to catch. A backup button that
 * silently does nothing is worse than no backup button, because the surveyor
 * walks away believing they have a copy.
 *
 * So the SHARE SHEET is tried first where the browser offers it. It is also the
 * better answer on its own merits: it is the control that iOS users already know
 * for "put this somewhere", and it reaches Files, AirDrop, Mail and Messages
 * without this app needing to know about any of them. `<a download>` remains for
 * every desktop browser and for Android, where it works and the share sheet
 * often does not.
 *
 * ── Cancelling is not failing ───────────────────────────────────────────────
 * A surveyor who opens the share sheet and thinks better of it has not hit an
 * error, and must not be shown one. `AbortError` — and Safari's habit of
 * throwing `NotAllowedError` for the same gesture — come back as `cancelled`,
 * which the screen says nothing about.
 */

import { buildTransfer } from '../../db/transfer';
import type { SurveyStore } from '../../db/store';

export type DeliveryOutcome = 'shared' | 'downloaded' | 'cancelled';

interface ShareCapableNavigator {
  share?: (data: { files?: File[]; title?: string }) => Promise<void>;
  canShare?: (data: { files?: File[] }) => boolean;
}

/**
 * Hand a blob to the surveyor as a named file.
 *
 * Throws only when BOTH routes fail, which is a real failure the screen has to
 * report — the alternative is a button that appears to work.
 */
export async function deliverFile(blob: Blob, fileName: string): Promise<DeliveryOutcome> {
  const nav = navigator as unknown as ShareCapableNavigator;

  if (typeof File === 'function' && typeof nav.share === 'function') {
    const file = new File([blob], fileName, { type: blob.type });
    // `canShare` is asked because Safari refuses some file types outright, and a
    // refusal from `share()` after the sheet has opened is a worse experience
    // than never opening it.
    if (nav.canShare?.({ files: [file] }) === true) {
      try {
        await nav.share({ files: [file], title: fileName });
        return 'shared';
      } catch (cause) {
        const name = cause instanceof Error ? cause.name : '';
        if (name === 'AbortError' || name === 'NotAllowedError') return 'cancelled';
        // Anything else means the sheet itself failed; fall through to the link
        // rather than telling a surveyor their backup did not happen when the
        // second route would have worked.
      }
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return 'downloaded';
  } finally {
    // Not immediate: revoking before the browser has started reading the blob
    // cancels the download on some engines. One tick is enough and the object
    // is released either way, so nothing leaks for the life of the tab.
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 60_000);
  }
}

export interface SurveyBackupOutcome {
  delivery: DeliveryOutcome;
  /** What to tell the surveyor. Null exactly when `delivery` is 'cancelled' — a cancelled share sheet says nothing (see above). */
  message: string | null;
}

/**
 * Build a backup file for exactly these surveys and hand it off (D-020, Task 3).
 *
 * The ONE place `buildTransfer` and `deliverFile` are composed, so "Save a
 * backup of everything" on the device screen (`SaveDeviceBackup`) and the
 * swipeable row's "Back up" action on Home say the same thing about what
 * landed, because they both call this. (A survey's own screen once had a
 * third caller, `SaveSurveyCopy` — the final review's fix wave, round 7,
 * deleted it: it was reached from no screen.) `deliver` is swappable only so
 * the unit suite can prove `buildTransfer`'s output without a DOM to hand a
 * Blob to — every real caller uses the default, `deliverFile`.
 */
export async function saveSurveyBackup(
  store: SurveyStore,
  surveyIds: readonly string[],
  deliver: (blob: Blob, fileName: string) => Promise<DeliveryOutcome> = deliverFile,
): Promise<SurveyBackupOutcome> {
  const file = await buildTransfer(store, surveyIds);
  const delivery = await deliver(file.blob, file.fileName);
  if (delivery === 'cancelled') return { delivery, message: null };
  return {
    delivery,
    message:
      `Saved ${file.fileName} — ${String(file.surveys)} ` +
      `${file.surveys === 1 ? 'survey' : 'surveys'}, ${String(file.photos)} ` +
      `${file.photos === 1 ? 'photo' : 'photos'}. Keep it somewhere you can find it again.`,
  };
}
